import React, {useState, useRef, useEffect} from 'react';
import {Box, Card, CardContent, Typography} from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import CompetencyTable from '../components/CompetenciesTable.comp';
import ChatBot from "../components/ChatBot.comp";
import {TARGET_AGE} from "../../../utils/META/cat_109";
import ChatForm from "../components/ChatForm.comp";
import DQProgramSummary from "../components/DQProgramSummary.comp";
import {REGIONS} from "../../../utils/META/cat_101";
import Button from "@mui/material/Button";
import {SUBJECTS} from "../../../utils/subjects";
import {IconButton} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";


const useStyles = makeStyles((theme) => ({
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '88vh',
        padding: '32px',
        [theme.breakpoints.down('sm')]: {
            padding: '16px',
        },
    },
    contentContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        marginBottom: (props) => props.chatInitiated ? '0' : '120px',
        transition: 'all 0.3s ease',
    },
    title: {
        background: 'linear-gradient(to right, #6A8EC9 10%, #8C74A7 35%, #D44C71 59%, #C91C6E 100%)',
        color: 'transparent',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: '500 !important',
        fontSize: '45px !important',
        marginBottom: '24px !important',
        [theme.breakpoints.down('sm')]: {
            fontSize: "28px !important",
        },
    },
    subtitle: {
        color: '#ED6628',
        fontSize: '26px',
        marginBottom: '16px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '17px',
        },
    },
    examplesContainer: {
        padding: '24px',
        margin: '24px 0',
        maxWidth: '800px',
        display: (props) => props.chatInitiated ? 'none' : 'block',
        transition: 'all 0.3s ease',
    },
    chatContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        overflowY: 'auto',
        padding: '10px',
    },
    thinkingIndicator: {
        alignSelf: 'flex-start',
        padding: '12px 16px',
        color: '#666',
        fontStyle: 'italic',
    },
    optionButton: {
        width:'18%',
        height:'100%',
        borderRadius:'16px',
        fontSize: '14px',
        textTransform: 'none',
        backgroundColor: '#FE686F !important',
    },
    optionsContainer: {
        display: 'flex',
        flexWrap:'wrap',
        justifyContent: 'flex-start',
        gap: '10px',
        marginTop: '15px',
    },
    chatBubble: {
        padding: '5px 15px',
        borderRadius: '12px',
        maxWidth: '75%',
        fontSize: '16px',
        overflow:'unset !important',
    },
    botChat: {
        backgroundColor: '#fce5cd !important',
        alignSelf: 'flex-start',
        textAlign: 'left',
        marginTop:'15px',
        marginBottom:'20px',
    },
    userChat: {
        display: 'flex',
        backgroundColor: '#f4cccc !important',
        alignSelf: 'flex-end',
        textAlign: 'left',
        marginTop:'20px',
    },
    editContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginTop: '5px',
        height:'45px',
    },
    editIcon: {
        marginLeft: '10px',
        cursor: 'pointer',
        color: '#EE6628 !important',
    },
}));

const DQProProgCreationCopy = () => {
    const classes = useStyles();

    const programCreationChat = [
        {
            id: 1,
            role: "bot",
            text: "Great! Based on your input, here are the AI Literacy competencies aligned with the IEEE DQ Global Standards (IEEE 3527.1™) and other major frameworks.\n\nPlease review and select the competencies that match your program goals.\n\nYou may select more than one.\n\nIf none matches your needs, click NONE OF THE ABOVE to further describe your learning themes to find more relevant competencies",
            type: "selection_table",
        },
        {
            id: 2,
            role: "bot",
            text: "No problem! Please provide more specific details about the learning themes or Issues you'd like to explore within AI Literacy." +
                "\n\nExamples you can describe:\n• Social media and mental health\n• Screen time and digital wellbeing\n• Fake news and critical reasoning\n• AI risks, bias, and ethical misuse\n•Understanding basic algorithms and automation",
            type: "text",
        },
        {
            id: 3,
            role: "bot",
            text: "Thanks! Here is the updated list of AI Literacy competencies based on your refined theme. \n\nPlease select the ones that match your goals. You may select more than one.",
            type: "selection_table",
        },
        {
            id: 4,
            role: "bot",
            text: "What is target age range?",
            type: "form",
            fields: [
                { label: "Target Audience Age", name: "targetAudAge",  type: "select",  options: TARGET_AGE },
            ]
        },
        {
            id: 5,
            role: "bot",
            text: "What is the target country? (e.g, Singapore, South Korea, United States of America)",
            type: "form",
            fields: [
                {  label: "Country", name: "country",  type: "select",  options: REGIONS },
            ]
        },
        {
            id: 6,
            role: "bot",
            text: "How would you like to implement this Al Literacy program? Please select one.",
            options: ["Integrate into existing subjects (e.g., Math, English, Science)", "Develop as a standalone Al Literacy curriculum"]
        },
        {
            id: 7,
            role: "bot",
            text: "Great! Which subject area should this program be connected to? Please select one.",
            type: "form",
            fields: [
                {  label: "Subject", name: "subject",  type: "select",  options: SUBJECTS },
            ]
        },
        {
            id: 8,
            role: "bot",
            text: "To support subject integration, could you specify a background learning topic from your selected subject(s) that could provide a relevant context for teaching Al Literacy?" +
                "\n\nFor example:\n“I’m teaching data interpretation in Mathematics and want to integrate the topic of disinformation,”\nor\n“I’m covering essay writing in English and want to explore the issue of Al misuse in academic cheating.”" +
                "\nPlease describe the learning topic or unit where Al Literacy would be most relevant.",
            type: "text",
        },
        {
            id: 9,
            role: "bot",
            text: "To help us plan the pacing and depth of your program, could you tell us how you'd like it to be structured?\n" +
                "Choose one of the following:",
            options: [
                "A single-session program\n(e.g, one 45-minute class or workshop.",
                "A multi-session course\n(e.g, 3-6 sessions,weekly or by unit)",
                "I’m not sure\n— please suggest a structure based on the topic and goals",
            ]
        },
        {
            id: 10,
            role: "bot",
            text: "How long would you like the session to be? This will help us plan the content depth and pacing appropriately.",
            options: [
                "Less than 30minutes\n" +
                "This session will serve as a short segment or part of another subject area",
                "30 minutes",
                "45 minutes",
                "1 hour",
                "Other",
            ]
        },
        {
            id: 11,
            role: "bot",
            text: "Please Specify.",
            type: "text",
        },
        {
            id: 12,
            role: "bot",
            type: "form",
            text: "Thank you! To tailor the program to your learning priorities, please rate the importance of each of the following focus areas.",
            fields: [
                {
                    name: "understanding_problems",
                    label: "Understanding the problems and issues related to the topic",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                },
                {
                    name: "exploring_solutions",
                    label: "Exploring responses and solutions",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                },
                {
                    name: "positive_mindset",
                    label: "Encouraging a positive mindset",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                }
            ]
        },
        {
            id: 13,
            role: "bot",
            type: "form",
            text: "Could you clarify which outcomes matter most? Please rate the importance of each of the following focus areas.",
            fields: [
                {
                    name: "raising_awareness",
                    label: "Raising awareness and acquiring factual knowledge",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                },
                {
                    name: "conceptual_understanding",
                    label: "Gaining conceptual understanding",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                },
                {
                    name: "practical_skills",
                    label: "Developing practical and procedural skills",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                }
            ]
        },
        {
            id: 14,
            role: "bot",
            type: "form",
            text: "Moreover, please specify which mindset areas you want to emphasize. Please rate the importance of each of the following focus areas.",
            fields: [
                {
                    name: "positive_attitudes",
                    label: "Shaping positive attitudes",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                },
                {
                    name: "strengthening_values",
                    label: "Strengthening values and beliefs",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                },
                {
                    name: "discovering_intentions",
                    label: "Discovering learners’ intentions and willingness to change",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                },
                {
                    name: "encouraging_self_reflection",
                    label: "Encouraging self-reflection and behavioral habits",
                    type: "radio",
                    options: ["Not Important At All", "Important", "Very Important"]
                }
            ]
        },
        {
            id: 15,
            role: "bot",
            text: "To ensure we pitch the content at the right level, have your learners had prior exposure to this topic?",
            options: [
                "No prior exposure",
                "Some informal exposure\n (e.g., social media, peer discussions)",
                "Formal exposure\n  (e.g., previous classroom learning)",
                "Uncertain",
            ]
        },
        {
            id: 16,
            role: "bot",
            text: "That’s helpful! Could you briefly describe what your learners have already encountered? (e.g., key concepts, formats used, depth of discussion)",
            type: "text",
        },
        {
            id: 17,
            role: "bot",
            text: "How would you like the learning experience to be delivered? Please select all formats that best match your goals:",
            options: [
                "Self-paced individual e-learning\n (digital modules learner complete on their own)",
                "Guided lecture or teacher-led session\n (instructor delivers content directly)",
                "Collaborative group actively\n (e.g., discussion,debate, role-play, or simulation)",
                "Individual task or reflection\n (e.g., journaling, worksheets, quiet reflection)",
                "Other",
            ]
        },
        {
            id: 18,
            role: "bot",
            text: "Please Describe any additional format or preferences",
            type: "text",
        },

        {
            id: 19,
            role: "bot",
            text: "What kind of tech environment will your learners have during the sessions?\n" +
                "Select one option that fits to your class tech environment:",
            options: [
                "Only the teacher/instructor has a device, projecting slides or materials; learners do not have personal device access.",
                "Both learners and instructors have device access during class.",
                "Learners will have occasional access to devices (e.g., lab days), but most sessions rely on teacher-led projection.",
                "Neither teachers nor learners will use digital devices during class.",
            ]
        },
        {
            id: 20,
            role: "bot",
            text: "Great! What teaching methods would you like to include in this session?\n"+
                "Select up to 3 options that best match your learning goals.",
            options: [
                "Lecture",
                "Storytelling or Real-Life Examples",
                "Case Study or Scenario",
                "Group Discussion or Debate",
                "Simulation or Role-Play",
                "Journaling or Personal Reflection",
                "Interactive Quiz or Poll",
                "Creative Froject (e.g., poster, art, music)",
                "Others",
            ]
        },
        {
            id: 21,
            role: "bot",
            text: "Before we move forward, we want to ensure your program is culturally appropriate and contextually respectful.\n" +
            "Are there any specific sensitivities, norms, or values we should be aware of when designing your program?",
            options: [
                "Religious or cultural taboos (e.g, language, dress, behavior)",
                "Local laws or policies (e.g., on student data, online privacy, Al use)",
                "Norms around gender roles, hierarchy, or authority",
                "Parental or community concerns around controversial issues",
                "None that I'm aware of",
                "Others",
            ]
        },
        {
            id: 22,
            role: "bot",
            text: "Thank you! Since you selected one or more sensitivities, could you please share a bit more context to help us design the session respectfully?"+
                "\n\nExamples:\n• “Avoid political topics,”\n• “Use visuals appropriate for Islamic schools,”\n• “Respect norms about boys and girls working in separate groups.”\n• “We avoid asking students to submit photos, especially those with faces or names visible.”",
            type: "text",
        },
        {
            id: 23,
            role: "bot",
            type: "summary",
            text: "Thank you! Based on your responses, here’s a summary of your program design. Please review carefully. If\n" +
                "you'd like to edit any section, you can return to that step to make changes by clicking the pencil edit button.",
        }


    ];
    // Chat state
    const [chats, setChats] = useState([]);
    const [input, setInput] = useState('');
    const [showChat, setShowChat] = useState(false);
    const [isBotThinking, setIsBotThinking] = useState(false);
    const [selectedCompetencies, setSelectedCompetencies] = useState([]);

    const [responses, setResponses] = useState({});
    const chatEndRef = useRef(null);

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [chats]);

    const handleOptionClick = (option) => {
        setChats((prevChats) => {
            const newChats = [...prevChats, {
                role: "user",
                text: option,
                isSingleResponse: true
            }];

            const lastBotQuestion = prevChats.findLast(chat => chat.role === "bot");
            const currentIndex = programCreationChat.findIndex(q => q.id === lastBotQuestion?.id);

            // Store structured response
            if (lastBotQuestion) {
                setResponses(prev => ({
                    ...prev,
                    [lastBotQuestion.id]: option
                }));
            }

            let nextQuestion;
            if (lastBotQuestion?.id === 10) {
                nextQuestion = (option === "Other")
                    ? programCreationChat.find(q => q.id === 11)
                    : programCreationChat.find(q => q.id === 12);
            } else {
                nextQuestion = programCreationChat[currentIndex + 1];
            }

            if (nextQuestion) {
                newChats.push({ ...nextQuestion, role: "bot" });
            }

            return newChats;
        });
    };

    const handleInputSubmit = (formData) => {
        setChats((prevChats) => {
            const newChats = [...prevChats];
            const lastBotMessage = [...prevChats].reverse().find(chat => chat.role === "bot");

            if (!lastBotMessage) return prevChats;

            const lastBotQuestion = programCreationChat.find(q => q.id === lastBotMessage.id);
            if (!lastBotQuestion) return prevChats;

            // Save structured responses
            setResponses(prev => ({
                ...prev,
                [lastBotQuestion.id]: formData
            }));

            const hasFields = lastBotQuestion.fields?.length > 0;
            const hasOptions = lastBotQuestion.options?.length > 0;

            if (hasFields) {
                const isMultiFieldForm = lastBotQuestion.fields.length > 1;

                if (isMultiFieldForm) {
                    const formattedResponse = Object.entries(formData)
                        .filter(([key]) => key !== 'file')
                        .map(([key, value]) => {
                            const field = lastBotQuestion.fields.find(f => f.name === key);
                            return field ? `${field.label}: ${value}` : null;
                        })
                        .filter(Boolean)
                        .join('\n');

                    newChats.push({
                        role: "user",
                        text: formattedResponse || "No input provided",
                        isFormResponse: true
                    });
                } else {
                    const field = lastBotQuestion.fields[0];
                    const value = formData[field.name];
                    newChats.push({
                        role: "user",
                        text: value ? `${field.label}: ${value}` : "No selection made",
                        isSingleResponse: true
                    });
                }
            } else if (hasOptions) {
                const selectedOption = formData.selectedOption;
                newChats.push({
                    role: "user",
                    text: selectedOption || "No option selected",
                    isSingleResponse: true
                });
            }

            const currentIndex = programCreationChat.findIndex(q => q.id === lastBotQuestion.id);
            const nextQuestion = programCreationChat[currentIndex + 1];

            if (nextQuestion) {
                newChats.push({ ...nextQuestion, role: "bot" });
            }

            return newChats;
        });
    };

    const handleTableSubmit = (selected) => {
        setChats((prevChats) => {
            const newChats = [...prevChats];
            const lastBotMessage = [...prevChats].reverse().find(chat => chat.role === "bot");
            if (!lastBotMessage) return prevChats;

            const lastBotQuestion = programCreationChat.find(q => q.id === lastBotMessage.id);
            if (!lastBotQuestion) return prevChats;

            // Save response
            setResponses(prev => ({
                ...prev,
                [lastBotQuestion.id]: selected
            }));

            let userResponseText = "No selection made";
            if (selected === 'none') {
                userResponseText = "None of the above";
            } else if (Array.isArray(selected) && selected.length > 0) {
                const titles = {
                    'DQ.AI.1': 'Recognizing AI Application',
                    'DQ.ETH.3': 'Ethical Use of AI Tools'
                };
                userResponseText = selected.map(code => titles[code] || code).join(', ');
            }

            newChats.push({
                role: "user",
                text: userResponseText
            });

            let nextQuestion = null;
            if (selected === 'none') {
                if (lastBotQuestion.id === 1 || lastBotQuestion.id === 3) {
                    nextQuestion = programCreationChat.find(q => q.id === 2);
                } else if (lastBotQuestion.id === 2) {
                    nextQuestion = programCreationChat.find(q => q.id === 3);
                }
            } else {
                nextQuestion = programCreationChat.find(q => q.id === 4);
            }

            if (nextQuestion) {
                newChats.push({ ...nextQuestion, role: "bot" });
            }

            return newChats;
        });
    };

    const handleRestartChatFromIndex = (index) => {
        setChats((prevChats) => {
            const updatedChats = prevChats.slice(0, index);
            let botMessageIndex = updatedChats.length - 1;

            while (botMessageIndex >= 0 &&
            (updatedChats[botMessageIndex].role !== "bot" ||
                !updatedChats[botMessageIndex].options)) {
                botMessageIndex--;
            }

            if (botMessageIndex >= 0) {
                const botMessage = programCreationChat.find(q => q.id === updatedChats[botMessageIndex].id);
                if (botMessage) {
                    updatedChats[botMessageIndex] = { ...botMessage };
                }
            }

            return updatedChats;
        });
    };

    const handleInputChange = (e) => setInput(e.target.value);

    const handleSendClick = () => {
        if (!input.trim()) return;

        setChats(prev => [...prev, { role: 'user', text: input }]);

        const lastBotMessage = [...chats].reverse().find(chat => chat.role === "bot");
        const lastBotQuestion = lastBotMessage
            ? programCreationChat.find(q => q.id === lastBotMessage.id)
            : null;

        if (lastBotQuestion) {
            setResponses(prev => ({
                ...prev,
                [lastBotQuestion.id]: input
            }));
        }

        setInput('');
        setShowChat(true);
        setIsBotThinking(true);

        setTimeout(() => {
            let nextQuestion;
            if (lastBotQuestion) {
                const currentIndex = programCreationChat.findIndex(q => q.id === lastBotQuestion.id);
                nextQuestion = programCreationChat[currentIndex + 1];
            } else {
                nextQuestion = programCreationChat[0];
            }

            setChats(prev => [...prev, { ...nextQuestion, role: "bot" }]);
            setIsBotThinking(false);
        }, 1500);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendClick();
        }
    };

    const handleEditClick = (questionId) => {
        setChats((prevChats) => {
            const index = prevChats.findIndex(c => c.id === questionId);
            return prevChats.slice(0, index);
        });
    };


    return (
        <Box className={classes.pageContainer}>
            {!showChat && (
                <Box className={classes.contentContainer}>
                    <Typography variant="h2" className={classes.title} sx={{ mb: 2 }}>
                        Welcome to DQ Learning Creator!
                    </Typography>

                    <Box className={classes.examplesContainer}>
                        <Typography variant="h5" sx={{ textAlign: 'center', mb: 4 }}>
                            Let's begin designing your custom AI Literacy learning Program.<br/>To start please tell us about the main topic or theme you want to teach.
                        </Typography>
                        <Typography variant="h5" style={{ color:'#EE6D29' }}>
                            Examples:
                        </Typography>
                        <Typography variant="h5" style={{ marginBottom: '15px', fontStyle:'italic', color:'#EE6D29' }}>
                            "I want to create a program to teach about cyberbullying for Grade 5 students." <br/>
                            "I want to explore AI Ethics with middle school learners."
                        </Typography>
                    </Box>
                </Box>
            )}
            {showChat && (
                <Box className={classes.chatContainer}>
                    {chats.map((chat, index) => (
                        <Card
                            key={index}
                            className={`${classes.chatBubble} ${chat.role === "bot" ? classes.botChat : classes.userChat}`}
                        >
                            {chat.role === "user" && (
                                <Box className={classes.editContainer}>
                                    <IconButton className={classes.editIcon} onClick={() => handleRestartChatFromIndex(index)}>
                                        <EditIcon />
                                    </IconButton>
                                </Box>
                            )}
                            <CardContent>
                                <Typography style={{ whiteSpace: "pre-line" }}>
                                    {chat.text}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}

                    {isBotThinking && (
                        <Box className={classes.thinkingIndicator}>
                            Bot is thinking...
                        </Box>
                    )}

                    {chats.length > 0 && chats[chats.length - 1].type === "selection_table" && (
                        <CompetencyTable
                            onNoneSelected={() => handleTableSubmit('none')}
                            onSubmit={(selected) => handleTableSubmit(selected)}
                            selectedCompetencies={selectedCompetencies}
                            setSelectedCompetencies={setSelectedCompetencies}
                        />
                    )}

                    {chats.length > 0 && chats[chats.length - 1].type === "text" && (
                        <ChatBot
                            chatHistory={chats}
                            input={input}
                            onInputChange={handleInputChange}
                            onInputKeyDown={handleInputKeyDown}
                            onSendClick={handleSendClick}
                            showChat={false}
                            isBotThinking={isBotThinking}
                        />
                    )}

                    {chats.length > 0 && chats[chats.length - 1].role === "bot" && chats[chats.length - 1].options && (
                        <Box className={classes.optionsContainer}>
                            {chats[chats.length - 1].options.map((option, idx) => (
                                <Button
                                    key={idx}
                                    variant="contained"
                                    className={classes.optionButton}
                                    onClick={() => handleOptionClick(option)}
                                >
                                    {option}
                                </Button>
                            ))}
                        </Box>
                    )}

                    {chats.length > 0 && chats[chats.length - 1].type === "form" && (
                        <Card className={`${classes.chatBubble} ${classes.botChat}`} style={{ width: "fit-content", maxWidth: "80%" }}>
                            <CardContent>
                                <ChatForm
                                    fields={chats[chats.length - 1].fields}
                                    onSubmit={handleInputSubmit}
                                />
                            </CardContent>
                        </Card>
                    )}

                    {chats.length > 0 && chats[chats.length - 1].type === "summary" && (
                        <DQProgramSummary responses={responses} onEdit={handleEditClick} />
                    )}


                    <div ref={chatEndRef} />
                </Box>
            )}

            {/* Only show input when needed */}
            {!showChat && (
                <Box>
                    <ChatBot
                        chatHistory={chats}
                        input={input}
                        onInputChange={handleInputChange}
                        onInputKeyDown={handleInputKeyDown}
                        onSendClick={handleSendClick}
                        showChat={showChat}
                        isBotThinking={isBotThinking}
                    />
                </Box>
            )}
        </Box>
    );
};

export default DQProProgCreationCopy;